import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePanel } from 'contexts/panelContext';
import { useKnowledgeBase } from 'components/knowledgeBase/knowledgeBaseContext';
import { useProgramTranslation } from 'hooks/useProgramTranslation';
import { MenuItem } from 'primereact/menuitem';
import './UserPanelBreadcrumbs.scss';

type BreadcrumbItem = { label: string; to?: string; item?: MenuItem };
type BreadcrumbDetail = { pathname: string; items: BreadcrumbItem[] };
const BreadcrumbContext = createContext<{
	detail: BreadcrumbDetail | null;
	setDetail: React.Dispatch<React.SetStateAction<BreadcrumbDetail | null>>;
	hiddenPath: string | null;
	setHiddenPath: React.Dispatch<React.SetStateAction<string | null>>;
}>({ detail: null, setDetail: () => {}, hiddenPath: null, setHiddenPath: () => {} });

export const UserPanelBreadcrumbsProvider = ({ children }: PropsWithChildren) => {
	const [detail, setDetail] = useState<BreadcrumbDetail | null>(null);
	const [hiddenPath, setHiddenPath] = useState<string | null>(null);
	return (
		<BreadcrumbContext.Provider value={{ detail, setDetail, hiddenPath, setHiddenPath }}>
			{children}
		</BreadcrumbContext.Provider>
	);
};

export const useUserPanelPageScrolled = (containerId: string, enabled = true) => {
	const { pathname } = useLocation();
	const { setHiddenPath } = useContext(BreadcrumbContext);
	const [scrollState, setScrollState] = useState({ pathname, scrolled: false });
	const scrolled = enabled && scrollState.pathname === pathname && scrollState.scrolled;
	useEffect(() => {
		if (!enabled) return undefined;
		const update = () => {
			const top = document.getElementById(containerId)?.scrollTop || 0;
			setScrollState((previous) => {
				// Keep the compact header stable when hiding breadcrumbs expands the viewport.
				const wasScrolled = previous.pathname === pathname && previous.scrolled;
				const next = top > (wasScrolled ? 4 : 64);
				return previous.pathname === pathname && previous.scrolled === next
					? previous
					: { pathname, scrolled: next };
			});
		};
		update();
		document.addEventListener('scroll', update, true);
		return () => document.removeEventListener('scroll', update, true);
	}, [containerId, enabled, pathname]);
	useEffect(() => {
		setHiddenPath(scrolled ? pathname : null);
		return () => setHiddenPath((previous) => (previous === pathname ? null : previous));
	}, [pathname, scrolled, setHiddenPath]);
	return scrolled;
};

export const useUserPanelBreadcrumbDetail = (label?: string, parentLabel?: string, parentTo?: string) => {
	const { pathname } = useLocation();
	const { setDetail } = useContext(BreadcrumbContext);
	useEffect(() => {
		if (!label) return undefined;
		const items = [...(parentLabel ? [{ label: parentLabel, to: parentTo }] : []), { label }];
		setDetail({ pathname, items });
		return () => setDetail((previous) => (previous?.pathname === pathname ? null : previous));
	}, [label, parentLabel, parentTo, pathname, setDetail]);
};

export const UserPanelBreadcrumbs = () => {
	const { pathname } = useLocation();
	const { panelScopePath, userPairs, panelType } = usePanel();
	const { baseBreadcrumbModel = [] } = useKnowledgeBase();
	const { tProgram } = useProgramTranslation();
	const { detail, hiddenPath } = useContext(BreadcrumbContext);
	const segments = pathname.slice(panelScopePath.length).split('/').filter(Boolean);
	const isPairPage = segments[0] === 'pair';
	const section = isPairPage ? segments[2] : segments[0];
	const pair = isPairPage ? userPairs?.find(({ id }) => id === Number(segments[1])) : undefined;
	const pairPath = `${panelScopePath}/pair/${segments[1]}`;
	const labels: Record<string, string> = {
		'personal-details': tProgram('userPanel.navigation.personalDetails'),
		application: tProgram('misc.pageTitles.editApplication'),
		pairing: tProgram('userPanel.navigation.yourPairs'),
		browseMembers: tProgram(
			`userPanel.navigation.potentialMembers.${panelType === 'mentor' ? 'allPairMentees' : 'allPairMentors'}`
		),
		'knowledge-base': tProgram('knowledgeBase.title'),
		'linkedin-badge': tProgram('userPanel.navigation.linkedInBadge'),
		'mentor-development': tProgram('userPanel.navigation.mentorPlan'),
		contact: tProgram('userPanel.navigation.contact'),
		timeline: tProgram('userPanel.navigation.timeline'),
		contract: tProgram('userPanel.breadcrumbs.contract'),
		session: tProgram('userPanel.breadcrumbs.sessions'),
		'mentee-plan': tProgram('userPanel.navigation.mentoringPlan'),
		'final-report': tProgram('userPanel.navigation.finalReport'),
		'final-survey': tProgram('userPanel.finalSurvey.title'),
		'process-summary': tProgram('userPanel.navigation.processSummary'),
		'process-roadmap': tProgram('userPanel.processRoadmap.header'),
		userfiles: tProgram('userPanel.navigation.userFiles'),
	};
	if (hiddenPath === pathname || !segments.length || (!labels[section] && section !== 'pair-details')) return null;

	const items: BreadcrumbItem[] = [];
	if (pair) {
		const { application } = pair;
		const user = application?.programMembership?.user;
		const name =
			[application?.systemAnswers?.name || user?.firstName, application?.systemAnswers?.surname || user?.lastName]
				.filter(Boolean)
				.join(' ') ||
			(panelType === 'mentor' ? pair.stats?.menteeUserDisplayName : pair.stats?.mentorUserDisplayName);
		items.push({ label: `${tProgram('userPanel.todo.pairWith')}: ${name || ''}`, to: `${pairPath}/pair-details` });
	}
	if (section !== 'pair-details') {
		items.push({ label: labels[section], to: `${isPairPage ? pairPath : panelScopePath}/${section}` });
		if (section === 'knowledge-base') {
			items.push(
				...baseBreadcrumbModel.filter((item) => item.label).map((item) => ({ label: item.label!, item }))
			);
		}
		if (detail?.pathname === pathname) items.push(...detail.items);
	}

	return (
		<nav className="user-panel-breadcrumbs" aria-label={tProgram('userPanel.breadcrumbs.label')}>
			<ol>
				<li>
					<Link to={panelScopePath} aria-label={tProgram('userPanel.navigation.dashboard')}>
						<i className="pi pi-home" aria-hidden="true" />
					</Link>
				</li>
				{items.map(({ label, to, item }, index) => {
					const isCurrent = index === items.length - 1;
					let content: React.ReactNode = <span aria-current={isCurrent ? 'page' : undefined}>{label}</span>;
					if (!isCurrent && to) content = <Link to={to}>{label}</Link>;
					else if (!isCurrent && item?.command) {
						content = (
							<button type="button" onClick={(event) => item.command?.({ originalEvent: event, item })}>
								{label}
							</button>
						);
					}
					return (
						<li key={to || item?.id || label}>
							<i className="pi pi-angle-right" aria-hidden="true" />
							{content}
						</li>
					);
				})}
			</ol>
		</nav>
	);
};
